import type {SyntheticEvent} from 'react';
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	Button,
	Divider,
	Flex,
	FormControl,
	FormLabel,
	IconButton,
	Input,
	Portal,
	Scroller,
	Switch,
	useDisclosure,
	VStack,
} from '@tempo/core';
import {BugOutlinedIcon} from '@tempo/icons';
import storage from '@deezer/next/shared/utils/storage';
import useToast from '@deezer/next/shared/hooks/useToast';

import D4CToolsStorage from './D4CToolsStorage';
import {CONFIG_OPTIONS, GROUP_LABELS} from './ToolOptions';
import type {
	BooleanConfigKeys,
	ConfigKeys,
	ConfigValue,
	SelectInput,
	ToolButton,
} from './types';
import {D4CToolsContext} from './D4CToolsContext';
import Popover, {
	PopoverArrow,
	PopoverBody,
	PopoverCloseButton,
	PopoverContent,
	PopoverHeader,
	PopoverTrigger,
} from '@components/Popover';
import Select from '@components/Select';

const D4C_TOOLS = 'd4c_tools';

type Point = {x: number; y: number};

const vectorDistance = (v1: Point, v2: Point) => {
	const a = v1.x - v2.x;
	const b = v1.y - v2.y;
	return Math.sqrt(a ** 2 + b ** 2);
};

const DRAG_THRESHOLD_PX = 20;
const TOP_BOUND_PX = 26;
const BOTTOM_BOUND_PX = 35;
const RIGHT_OFFSET_PX = 16;

const easterPattern = [
	'ArrowUp',
	'ArrowUp',
	'ArrowDown',
	'ArrowDown',
	'ArrowLeft',
	'ArrowRight',
	'ArrowLeft',
	'ArrowRight',
	'b',
	'a',
] as const;

type DragProps = {
	dragStartLeft: number;
	dragStartTop: number;
	dragStartX: number;
	dragStartY: number;
};

const D4CTools = () => {
	const toast = useToast();
	const {config, setConfig} = useContext(D4CToolsContext);
	const {onOpen, onClose, isOpen} = useDisclosure();
	const [isToolVisible, setIsToolVisible] = useState(false);

	const buttonRef = useRef<HTMLButtonElement>(null);

	const isDraggingRef = useRef(false);

	const dragPropsRef = useRef<DragProps>({
		dragStartLeft: 0,
		dragStartTop: 0,
		dragStartX: 0,
		dragStartY: 0,
	});

	const easterCountRef = useRef(0);
	const easterTimeoutRef = useRef<number | undefined>(undefined);

	const handleStringChange = useCallback(
		(key: ConfigKeys) => (e: React.ChangeEvent<HTMLInputElement>) => {
			D4CToolsStorage.setConfigValue(key, e.target.value);
			setConfig(D4CToolsStorage.getConfig());
		},
		[setConfig],
	);

	const handleBooleanChange = useCallback(
		(key: BooleanConfigKeys) => (e: React.ChangeEvent<HTMLInputElement>) => {
			D4CToolsStorage.setConfigValue(key, e.target.checked);
			setConfig(D4CToolsStorage.getConfig());
		},
		[setConfig],
	);

	const handleSelectChange = useCallback(
		(key: ConfigKeys) => (value: string) => {
			D4CToolsStorage.setConfigValue(key, value);
			setConfig(D4CToolsStorage.getConfig());
		},
		[setConfig],
	);

	const getClientPoint = useCallback((e: MouseEvent | TouchEvent): Point => {
		if ('changedTouches' in e) {
			const t = e.type === 'touchend' ? e.changedTouches[0] : e.touches[0];
			return {x: t.clientX, y: t.clientY};
		}
		return {x: e.clientX, y: e.clientY};
	}, []);

	const applyTransform = useCallback((x: number, y: number) => {
		if (!buttonRef.current) return;
		buttonRef.current.style.transform = `translate(${x}px, ${y}px)`;
	}, []);

	const snapToSide = useCallback(
		(e: MouseEvent | TouchEvent) => {
			if (!buttonRef.current) return;

			const {dragStartLeft, dragStartTop, dragStartX, dragStartY} =
				dragPropsRef.current;

			const windowWidth = window.innerWidth;
			const {x: currX, y: currY} = getClientPoint(e);

			let nextY = currY;
			if (nextY < TOP_BOUND_PX) nextY = TOP_BOUND_PX;

			const maxY = document.documentElement.clientHeight - BOTTOM_BOUND_PX;
			if (nextY > maxY) nextY = maxY;

			const nextX =
				currX < windowWidth / 2
					? dragStartX
					: windowWidth - dragStartX - RIGHT_OFFSET_PX;

			applyTransform(
				dragStartLeft + nextX - dragStartX,
				dragStartTop + nextY - dragStartY,
			);
		},
		[applyTransform, getClientPoint],
	);

	const moveListenerRef =
		useRef<(e: MouseEvent | TouchEvent) => void>(undefined);
	const upListenerRef = useRef<(e: MouseEvent | TouchEvent) => void>(undefined);

	const detachListeners = useCallback((mode: 'mouse' | 'touch') => {
		const moveEvent = mode === 'mouse' ? 'mousemove' : 'touchmove';
		const upEvent = mode === 'mouse' ? 'mouseup' : 'touchend';

		if (moveListenerRef.current) {
			window.removeEventListener(moveEvent, moveListenerRef.current as any);
		}
		if (upListenerRef.current) {
			window.removeEventListener(upEvent, upListenerRef.current as any);
		}
	}, []);

	const attachListeners = useCallback((mode: 'mouse' | 'touch') => {
		const moveEvent = mode === 'mouse' ? 'mousemove' : 'touchmove';
		const upEvent = mode === 'mouse' ? 'mouseup' : 'touchend';

		if (moveListenerRef.current) {
			window.addEventListener(moveEvent, moveListenerRef.current as any, {
				passive: false,
			});
		}
		if (upListenerRef.current) {
			window.addEventListener(upEvent, upListenerRef.current as any, {
				passive: false,
			});
		}
	}, []);

	const onMove = useCallback(
		(e: MouseEvent | TouchEvent) => {
			if (!buttonRef.current) return;

			const {x: clientX, y: clientY} = getClientPoint(e);
			const {dragStartX, dragStartY, dragStartLeft, dragStartTop} =
				dragPropsRef.current;

			if (
				vectorDistance(
					{x: dragStartX, y: dragStartY},
					{x: clientX, y: clientY},
				) < DRAG_THRESHOLD_PX
			) {
				return;
			}

			isDraggingRef.current = true;
			onClose();

			applyTransform(
				dragStartLeft + clientX - dragStartX,
				dragStartTop + clientY - dragStartY,
			);
		},
		[applyTransform, getClientPoint, onClose],
	);

	const onUp = useCallback(
		(e: MouseEvent | TouchEvent) => {
			if (!buttonRef.current) return;

			if ('preventDefault' in e) e.preventDefault();
			if ('stopPropagation' in e) e.stopPropagation();

			const {x: clientX, y: clientY} = getClientPoint(e);
			const {dragStartX, dragStartY} = dragPropsRef.current;

			const mode: 'mouse' | 'touch' = 'changedTouches' in e ? 'touch' : 'mouse';
			detachListeners(mode);

			if (
				vectorDistance(
					{x: dragStartX, y: dragStartY},
					{x: clientX, y: clientY},
				) < DRAG_THRESHOLD_PX
			) {
				return;
			}

			snapToSide(e);

			buttonRef.current.style.transition = '0.3s ease-in-out transform';
		},
		[detachListeners, getClientPoint, snapToSide],
	);

	useEffect(() => {
		moveListenerRef.current = onMove;
		upListenerRef.current = onUp;
	}, [onMove, onUp]);

	useEffect(() => {
		return () => {
			detachListeners('mouse');
			detachListeners('touch');
		};
	}, [detachListeners]);

	const handleClick = useCallback((e: SyntheticEvent) => {
		if (!isDraggingRef.current) return;

		e.preventDefault();
		e.stopPropagation();
		isDraggingRef.current = false;
	}, []);

	const onPointerStart = useCallback(
		(
			e:
				| React.MouseEvent<HTMLButtonElement>
				| React.TouchEvent<HTMLButtonElement>,
		) => {
			if (!buttonRef.current) return;

			const isTouch = 'touches' in e;
			const clientX = isTouch
				? e.touches[0].clientX
				: (e as React.MouseEvent).clientX;
			const clientY = isTouch
				? e.touches[0].clientY
				: (e as React.MouseEvent).clientY;

			const {offsetTop, offsetLeft} = buttonRef.current;
			const {left, top} = buttonRef.current.getBoundingClientRect();

			dragPropsRef.current = {
				dragStartLeft: left - offsetLeft,
				dragStartTop: top - offsetTop,
				dragStartX: clientX,
				dragStartY: clientY,
			};

			const mode: 'mouse' | 'touch' = isTouch ? 'touch' : 'mouse';
			attachListeners(mode);

			buttonRef.current.style.transition = 'none';
		},
		[attachListeners],
	);

	useEffect(() => {
		const checkVisibility = () => {
			setIsToolVisible(Boolean(storage.get(D4C_TOOLS)));
		};

		const easterHandler = (event: KeyboardEvent) => {
			if (
				easterPattern.indexOf(event.key as any) < 0 ||
				event.key !== easterPattern[easterCountRef.current]
			) {
				easterCountRef.current = 0;
				return;
			}

			easterCountRef.current++;

			if (typeof easterTimeoutRef.current !== 'undefined') {
				window.clearTimeout(easterTimeoutRef.current);
				easterTimeoutRef.current = undefined;
			}

			if (easterPattern.length === easterCountRef.current) {
				easterCountRef.current = 0;

				if (storage.get(D4C_TOOLS)) {
					storage.set(D4C_TOOLS, undefined);
				} else {
					storage.set(D4C_TOOLS, true);
				}

				checkVisibility();
			} else {
				easterTimeoutRef.current = window.setTimeout(() => {
					easterCountRef.current = 0;
					easterTimeoutRef.current = undefined;
				}, 500);
			}
		};

		checkVisibility();

		window.addEventListener('storage', checkVisibility);
		window.addEventListener('keydown', easterHandler);

		return () => {
			window.removeEventListener('storage', checkVisibility);
			window.removeEventListener('keydown', easterHandler);
		};
	}, []);

	const content = useMemo(() => {
		if (!isToolVisible) {
			return {};
		}

		const getOptionElement = (key: string, input: ConfigValue) => {
			const {label, type} = input;
			const baseProps = {
				flex: 1,
				minWidth: {base: '100%', sm: '33%'},
			};

			if (type === 'boolean') {
				return (
					<FormControl
						key={key}
						display="flex"
						alignItems="center"
						{...baseProps}
					>
						<FormLabel htmlFor={key} mb="0" flex={1}>
							{label}
						</FormLabel>
						<Switch
							id={key}
							isChecked={JSON.parse(config[key as BooleanConfigKeys])}
							onChange={handleBooleanChange(key as BooleanConfigKeys)}
						/>
					</FormControl>
				);
			}

			if (type === 'select') {
				const {options, placeholder, onSelect} = input as SelectInput;

				return (
					<FormControl key={key} {...baseProps}>
						<FormLabel htmlFor={key} mb="0">
							{label}
						</FormLabel>
						<Select
							placeholder={placeholder}
							options={options}
							value={config[key as ConfigKeys]}
							onChange={value => {
								onSelect?.(value);
								handleSelectChange(key as ConfigKeys)(value);
							}}
						/>
					</FormControl>
				);
			}

			if (type === 'button') {
				const {onClick, buttonProps} = input as ToolButton;

				return (
					<Button
						key={key}
						variant="outline"
						onClick={() => onClick?.({toast})}
						{...buttonProps}
						{...baseProps}
					>
						{label}
					</Button>
				);
			}

			return (
				<FormControl key={key} {...baseProps}>
					<FormLabel htmlFor={key}>{label}</FormLabel>
					<Input
						id={key}
						value={config[key as ConfigKeys]}
						onChange={handleStringChange(key as ConfigKeys)}
					/>
				</FormControl>
			);
		};

		return Object.entries(CONFIG_OPTIONS).reduce<
			Record<string, React.ReactElement[]>
		>((acc, [key, input]) => {
			const optionElement = getOptionElement(key, input);

			if (input.groupId) {
				acc[input.groupId] = [...(acc[input.groupId] || []), optionElement];
			} else {
				acc[key] = [optionElement];
			}

			return acc;
		}, {});
	}, [
		config,
		handleBooleanChange,
		handleSelectChange,
		handleStringChange,
		isToolVisible,
		toast,
	]);

	if (!isToolVisible) {
		return null;
	}

	return (
		<Popover
			isOpen={isOpen}
			onOpen={onOpen}
			onClose={onClose}
			placement="right"
			closeOnBlur={true}
		>
			<PopoverTrigger>
				<IconButton
					ref={buttonRef}
					aria-label="D4C Tools"
					onClick={handleClick}
					icon={<BugOutlinedIcon />}
					position="fixed"
					bottom="250px"
					left={4}
					zIndex={9999}
					onMouseDown={onPointerStart}
					onTouchStart={onPointerStart}
					size="small"
				/>
			</PopoverTrigger>

			<Portal>
				<PopoverContent
					width={{base: 'xs', sm: 'lg'}}
					sx={{
						'*': {
							boxSizing: 'border-box',
						},
					}}
				>
					<PopoverArrow />
					<PopoverCloseButton />
					<PopoverHeader>D4C Tools</PopoverHeader>
					<PopoverBody>
						<Scroller maxHeight="400px">
							<VStack spacing={4} flex={1} pe={1} divider={<Divider />}>
								{Object.entries(content).map(([groupKey, options]) => {
									if (options.length === 1) {
										return options[0];
									}

									return (
										<FormControl key={groupKey}>
											<FormLabel
												htmlFor={groupKey}
												color="text.neutral.primary.default"
											>
												{GROUP_LABELS[groupKey]}
											</FormLabel>
											<Flex flexFlow="row wrap" gap={2}>
												{options}
											</Flex>
										</FormControl>
									);
								})}
							</VStack>
						</Scroller>
					</PopoverBody>
				</PopoverContent>
			</Portal>
		</Popover>
	);
};

export default D4CTools;
